/**
 * Griglia acquisti per rosa (stile Forza 4): una riga per partecipante,
 * colonne = slot rosa, pallino colorato per ruolo ad ogni acquisto.
 */

import { getRoleMacroGroup } from '../features/filters/index.js';
import { escapeHtml } from '../utils/escapeHtml.js';
import { opponentPlaceholder } from './auctionOpponents.js';
import {
  buildOpponentSpendRows,
  buildOwnSpendRow,
  sortPerRosaRows,
} from './opponentSpend.js';
import { primaryMantraRole } from './rosaCalc.js';
import { classicBucketsForPlayer, mantraBucketsForPlayer } from './roleBuckets.js';
import { resolveStudioPlayerMantraRoles } from './studioCalc.js';

function isValidPrice(price) {
  const n = Number(price);
  return Number.isFinite(n) && n >= 1;
}

function resolveAcquisitionRole(player, mode) {
  if (!player) return 'Altri';
  if (mode === 'classic') {
    const buckets = classicBucketsForPlayer(player);
    return buckets[0] || 'A';
  }
  const roles = resolveStudioPlayerMantraRoles(player);
  return primaryMantraRole(roles);
}

function sortAcquisitions(items) {
  return [...items].sort((a, b) => {
    const atDiff = (Number(a.at) || 0) - (Number(b.at) || 0);
    if (atDiff !== 0) return atDiff;
    return String(a.key).localeCompare(String(b.key));
  });
}

function acquisitionsFromRoster(roster, playersByKey, mode) {
  if (!(roster instanceof Map)) return [];
  const items = [];
  for (const [key, entry] of roster.entries()) {
    if (!isValidPrice(entry?.price)) continue;
    const player = playersByKey.get(key);
    items.push({
      key,
      price: Number(entry.price),
      at: Number(entry.at) || 0,
      name: player?.name || String(key).split('|')[0] || key,
      role: resolveAcquisitionRole(player, mode),
    });
  }
  return sortAcquisitions(items);
}

function acquisitionsFromExits(auctionExits, opponentId, playersByKey, mode) {
  if (!(auctionExits instanceof Map)) return [];
  const items = [];
  for (const [key, entry] of auctionExits.entries()) {
    if (!isValidPrice(entry?.price)) continue;
    if (String(entry?.opponentId || '') !== String(opponentId || '')) continue;
    const player = playersByKey.get(key);
    items.push({
      key,
      price: Number(entry.price),
      at: Number(entry.at) || 0,
      name: player?.name || String(key).split('|')[0] || key,
      role: resolveAcquisitionRole(player, mode),
    });
  }
  return sortAcquisitions(items);
}

function cellsFromAcquisitions(acquisitions, slots) {
  const cells = Array.from({ length: slots }, () => null);
  acquisitions.forEach((acq, index) => {
    if (index < slots) cells[index] = acq;
  });
  return cells;
}

/**
 * @returns {{ slots: number, rows: { id: string, name: string, isMe: boolean, owned: number, cells: object[] }[], hasData: boolean }}
 */
export function buildRosterAcquisitionGrid({
  roster,
  auctionExits,
  opponents = [],
  playersByKey = new Map(),
  mode = 'mantra',
  budget = 0,
  maxPlayers = 29,
  assigned = false,
} = {}) {
  const slots = Math.max(1, Number(maxPlayers) || 29);
  const spendRows = assigned
    ? sortPerRosaRows([
      buildOwnSpendRow(roster, { budget, maxPlayers: slots }),
      ...buildOpponentSpendRows(auctionExits, opponents, { budget, maxPlayers: slots })
        .filter((r) => !r.pile),
    ])
    : [buildOwnSpendRow(roster, { budget, maxPlayers: slots })];

  const rows = spendRows.map((spendRow) => {
    const isMe = spendRow.id === 'me';
    const acquisitions = isMe
      ? acquisitionsFromRoster(roster, playersByKey, mode)
      : acquisitionsFromExits(auctionExits, spendRow.id, playersByKey, mode);
    const oppIndex = opponents.findIndex((o) => String(o.id) === String(spendRow.id));
    const name = isMe
      ? 'Tu'
      : (String(spendRow.name || '').trim() || opponentPlaceholder(oppIndex >= 0 ? oppIndex : 0));
    return {
      id: spendRow.id,
      name,
      isMe,
      owned: acquisitions.length,
      cells: cellsFromAcquisitions(acquisitions, slots),
    };
  });

  const hasData = rows.some((row) => row.owned > 0);
  return { slots, rows, hasData };
}

export function renderRosterAcquisitionGrid(grid) {
  if (!grid?.hasData) {
    return `
      <section class="analytics-section" aria-labelledby="analytics-roster-grid-title">
        <h2 id="analytics-roster-grid-title" class="analytics-section-title">Griglia rose</h2>
        <p class="analytics-empty" data-testid="analytics-roster-grid-empty">
          La griglia apparirà dopo il primo acquisto registrato con prezzo.
        </p>
      </section>
    `;
  }

  const { slots, rows } = grid;
  const colHeaders = Array.from({ length: slots }, (_, i) => {
    const n = i + 1;
    const show = n === 1 || n === slots || n % 5 === 0;
    return `<th scope="col" class="analytics-roster-grid-slot${show ? '' : ' analytics-roster-grid-slot--muted'}">${show ? n : ''}</th>`;
  }).join('');

  const body = rows.map((row) => {
    const rowCls = row.isMe ? ' analytics-roster-grid-row--me' : '';
    const cells = row.cells.map((cell) => {
      if (!cell) {
        return `<td class="analytics-roster-grid-cell"><span class="analytics-roster-grid-dot is-empty" aria-hidden="true"></span></td>`;
      }
      const macro = getRoleMacroGroup(cell.role);
      const label = `${cell.name} · ${cell.price}M · ${cell.role}`;
      return `<td class="analytics-roster-grid-cell">
        <span class="analytics-roster-grid-dot analytics-roster-grid-dot--${macro}"
              title="${escapeHtml(label)}"
              aria-label="${escapeHtml(label)}"></span>
      </td>`;
    }).join('');
    return `
      <tr class="analytics-roster-grid-row${rowCls}" data-testid="analytics-roster-grid-row-${escapeHtml(row.id)}">
        <th scope="row" class="analytics-roster-grid-row-label">${escapeHtml(row.name)}</th>
        ${cells}
      </tr>
    `;
  }).join('');

  return `
    <section class="analytics-section analytics-section--roster-grid"
             aria-labelledby="analytics-roster-grid-title"
             data-testid="analytics-roster-grid">
      <div class="analytics-section-heading analytics-section-heading--split">
        <h2 id="analytics-roster-grid-title" class="analytics-section-title">Griglia rose</h2>
        <span class="analytics-section-meta">${slots} slot · ordine acquisti</span>
      </div>
      <p class="analytics-roster-grid-hint">Ogni riga è una rosa; ogni pallino è un acquisto, colorato per ruolo.</p>
      <div class="analytics-roster-grid-board">
        <table class="analytics-roster-grid-table">
          <thead>
            <tr>
              <th scope="col" class="analytics-roster-grid-corner"></th>
              ${colHeaders}
            </tr>
          </thead>
          <tbody>${body}</tbody>
        </table>
      </div>
    </section>
  `;
}
